/**
 * useProjectManager - Project action management
 *
 * Manages the Excalidraw initialState promise, excalidrawKey for
 * forcing remounts, project action processing (new/load/switch),
 * and canvas-needs-reset detection.
 *
 * Extracted from App.tsx lines 359–556
 */

import { useEffect, useRef, useState, type MutableRefObject } from "react";

import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { resolvablePromise } from "@excalidraw/common";
import { isInitializedImageElement } from "@excalidraw/element";

import type {
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from "@excalidraw/excalidraw/types";
import type { FileId } from "@excalidraw/element/types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import { updateStaleImageStatuses } from "../data/FileManager";
import { LocalData } from "../data/LocalData";

import type { ProjectAction } from "./useAppView";

interface UseProjectManagerParams {
  excalidrawAPIRef: MutableRefObject<ExcalidrawImperativeAPI | null>;
  projectAction: ProjectAction;
  clearProjectAction: () => void;
  currentView: "entry" | "canvas";
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  currentDiagramId: string | null;
  loadDiagram: (id: string) => Promise<any>;
}

export const useProjectManager = ({
  excalidrawAPIRef,
  projectAction,
  clearProjectAction,
  currentView,
  isAuthLoading,
  isAuthenticated,
  currentDiagramId,
  loadDiagram,
}: UseProjectManagerParams) => {
  const [needsCanvasReset, setNeedsCanvasReset] = useState(false);
  const [excalidrawKey, setExcalidrawKey] = useState(0);

  // initial state
  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });

  // Track if we've processed the current projectAction
  const processedProjectActionRef = useRef<ProjectAction>(null);

  // Reset initial state promise when new project is requested
  // This MUST happen synchronously before Excalidraw renders to take effect
  if (projectAction === "new" && processedProjectActionRef.current !== "new") {
    if (import.meta.env.DEV) {
      console.log(
        "[IdeaWeave] Resetting initialStatePromiseRef for new project (sync)",
      );
    }
    // Create a new promise that immediately resolves with empty data
    const newPromise = resolvablePromise<ExcalidrawInitialDataState | null>();
    newPromise.resolve({
      elements: [],
      appState: getDefaultAppState(),
      files: {},
    });
    initialStatePromiseRef.current.promise = newPromise;
    processedProjectActionRef.current = "new";
  } else if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  // Increment key to force Excalidraw remount when projectAction is "new"
  // Also reset processedProjectActionRef when action is cleared
  useEffect(() => {
    if (projectAction === "new") {
      setExcalidrawKey((prev) => prev + 1);
      if (import.meta.env.DEV) {
        console.log("[IdeaWeave] Incrementing excalidrawKey to force remount");
      }
    } else if (projectAction === null) {
      // Reset the processed flag so next "new" action will be detected
      processedProjectActionRef.current = null;
      if (import.meta.env.DEV) {
        console.log(
          "[IdeaWeave] Reset processedProjectActionRef for next action",
        );
      }
    }
  }, [projectAction]);

  // Handle project actions (new project, load project, etc.)
  useEffect(() => {
    const api = excalidrawAPIRef.current;
    if (!api || !projectAction) {
      return;
    }

    const actionType =
      typeof projectAction === "object" ? projectAction.type : projectAction;
    if (import.meta.env.DEV) {
      console.log(
        `[IdeaWeave] Processing project action:`,
        actionType,
        projectAction,
      );
    }

    const run = async () => {
      switch (actionType) {
        case "new":
          if (import.meta.env.DEV) {
            console.log("[IdeaWeave] Resetting scene for new project");
          }
          api.resetScene();
          break;

        case "load": {
          const diagramId =
            typeof projectAction === "object" && "diagramId" in projectAction
              ? projectAction.diagramId
              : null;
          if (diagramId) {
            const diagram = await loadDiagram(diagramId);
            if (diagram && excalidrawAPIRef.current) {
              excalidrawAPIRef.current.resetScene();
              excalidrawAPIRef.current.updateScene({
                elements: diagram.elements,
                captureUpdate: CaptureUpdateAction.IMMEDIATELY,
              });

              const currentFiles = excalidrawAPIRef.current.getFiles();
              const missingFileIds =
                diagram.elements?.reduce((acc: Set<FileId>, element: any) => {
                  if (
                    isInitializedImageElement(element) &&
                    !currentFiles[element.fileId]
                  ) {
                    acc.add(element.fileId);
                  }
                  return acc;
                }, new Set<FileId>()) || new Set<FileId>();

              if (missingFileIds.size) {
                const { loadedFiles, erroredFiles } =
                  await LocalData.fileStorage.getFiles([...missingFileIds]);
                if (loadedFiles.length) {
                  excalidrawAPIRef.current.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI: excalidrawAPIRef.current,
                  erroredFiles,
                  elements:
                    excalidrawAPIRef.current.getSceneElementsIncludingDeleted(),
                });
              }

              if (diagram.elements.length > 0) {
                excalidrawAPIRef.current.scrollToContent(diagram.elements, {
                  fitToContent: true,
                });
              }
            }
          }
          break;
        }

        case "switch":
          if (import.meta.env.DEV) {
            console.log("[IdeaWeave] Switch project - not yet implemented");
          }
          break;
      }
      clearProjectAction();
    };

    run();
  }, [excalidrawAPIRef, projectAction, clearProjectAction, loadDiagram]);

  // Detect when we need a fresh canvas: logged in, on canvas, no project selected
  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    const hasNoProject = !currentDiagramId;
    const navEntry = performance.getEntriesByType?.("navigation")?.[0] as
      | PerformanceNavigationTiming
      | undefined;
    const isPageReload = navEntry?.type === "reload";

    // Check if user just registered from canvas quota-exceeded flow
    // In that case, skip the canvas reset so their work is preserved
    const returnToCanvas = sessionStorage.getItem(
      "ideaweave:returnToCanvasAfterAuth",
    );

    if (
      isAuthenticated &&
      currentView === "canvas" &&
      hasNoProject &&
      !isPageReload
    ) {
      if (returnToCanvas) {
        // User registered from canvas - preserve their work
        if (import.meta.env.DEV) {
          console.log(
            "[IdeaWeave] Skipping canvas reset - user registered from canvas quota flow",
          );
        }
        // Clean up the flag and backup now that both hooks have read it
        sessionStorage.removeItem("ideaweave:returnToCanvasAfterAuth");
        sessionStorage.removeItem("ideaweave:canvasBackup:elements");
        sessionStorage.removeItem("ideaweave:canvasBackup:appState");
        setNeedsCanvasReset(false);
      } else {
        if (import.meta.env.DEV) {
          console.log(
            "[IdeaWeave] Need fresh canvas: logged in, on canvas, no project",
          );
        }
        setNeedsCanvasReset(true);
      }
    } else {
      setNeedsCanvasReset(false);
    }
  }, [isAuthLoading, isAuthenticated, currentView, currentDiagramId]);

  // Reset canvas when needed and excalidrawAPI is ready
  useEffect(() => {
    const api = excalidrawAPIRef.current;
    if (needsCanvasReset && api) {
      if (import.meta.env.DEV) {
        console.log("[IdeaWeave] Resetting canvas for fresh start");
      }
      api.resetScene();
      setNeedsCanvasReset(false);
    }
  }, [needsCanvasReset, excalidrawAPIRef]);

  return {
    excalidrawKey,
    initialStatePromiseRef,
  };
};
